/**
 * AssemblyLeaderboard — how the assemblies stand beside each other.
 *
 * One component, three readers: the campaign office, an assembly's own office
 * (which gets "you are here" marked), and the public appeal page. The figures
 * come from the same pledge records in every case, so nobody can be shown a
 * different story depending on which door they came in by.
 *
 * It carries assembly names and money only. No donor is named anywhere in it.
 */
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Trophy, Loader2, Info } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import api from '@/lib/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RANKINGS = [
  { value: 'received', label: 'Money received' },
  { value: 'pledged', label: 'Total pledged' },
  { value: 'target_share', label: 'Share of own target' },
];

const money = (n, ccy) => `${ccy} ${Number(n || 0).toLocaleString(undefined,
  { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const MEDALS = ['bg-amber-400 text-amber-950', 'bg-slate-300 text-slate-800',
                'bg-orange-400 text-orange-950'];

export const AssemblyLeaderboard = ({
  campaignId, campaignSlug, churchId, variant = 'admin', defaultRankBy = 'received',
}) => {
  const [rankBy, setRankBy] = useState(defaultRankBy);

  const { data, isLoading, error } = useQuery({
    queryKey: ['assembly-leaderboard', variant, campaignId || campaignSlug, churchId, rankBy],
    queryFn: async () => {
      if (variant === 'public') {
        const r = await fetch(
          `${API_URL}/api/campaigns/${campaignSlug || campaignId}/leaderboard/public?rank_by=${rankBy}`);
        if (!r.ok) throw new Error('unavailable');
        return r.json();
      }
      const path = variant === 'office'
        ? `/church/${churchId}/campaigns/${campaignId}/leaderboard`
        : `/campaigns/${campaignId}/assembly-breakdown`;
      const r = await api.get(path, { params: { rank_by: rankBy } });
      return r.data;
    },
    enabled: !!(campaignId || campaignSlug),
    retry: false,
  });

  if (isLoading) {
    return (
      <p className="flex items-center gap-2 p-4 text-sm text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" />Working out the standings…
      </p>
    );
  }
  if (error || !data) {
    // The office needs to know the board could not be read; a silent blank
    // reads as "nobody has given", which is a different thing entirely.
    if (variant === 'public') return null;
    return (
      <p className="p-3 text-sm text-rose-600" data-testid="leaderboard-error">
        The standings could not be loaded just now.
      </p>
    );
  }

  const rows = data.assemblies || [];
  const ccy = data.base_currency || 'USD';
  const mine = churchId ? String(churchId) : '';

  return (
    <div className="space-y-3" data-testid="assembly-leaderboard">
      <div className={variant === 'public'
        ? 'rounded-xl border border-slate-200 bg-white p-5 shadow-sm space-y-3'
        : 'space-y-3'}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        {variant === 'office' ? <span /> : (
          <p className="flex items-center gap-2 text-sm font-semibold text-slate-800">
            <Trophy className="h-4 w-4 text-amber-500" />
            How the assemblies stand
          </p>
        )}
        <Select value={rankBy} onValueChange={setRankBy}>
          <SelectTrigger className="h-8 w-[11rem] text-xs" data-testid="leaderboard-rank-by">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {RANKINGS.map((r) => (
              <SelectItem key={r.value} value={r.value}
                          data-testid={`leaderboard-rank-${r.value}`}>{r.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {rows.length ? (
        <ol className="space-y-1.5">
          {rows.map((a, i) => {
            const isMine = mine && a.church_id === mine;
            const pct = a.target_share;
            return (
              <li key={a.church_id || `row-${i}`}
                  data-testid={`leaderboard-row-${a.church_id || 'unattributed'}`}
                  className={`flex flex-wrap items-center gap-3 rounded-lg border px-3 py-2 ${
                    isMine ? 'border-amber-300 bg-amber-50' : 'border-slate-200 bg-white'}`}>
                <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full
                                  text-xs font-bold ${
                                    a.rank ? (MEDALS[a.rank - 1] || 'bg-slate-100 text-slate-600')
                                           : 'bg-slate-100 text-slate-400'}`}>
                  {a.rank ?? '–'}
                </span>
                <span className="min-w-[8rem] flex-1 text-sm font-medium text-slate-900">
                  {a.name}
                  {isMine ? <Badge className="ml-2 bg-amber-500 text-white">you</Badge> : null}
                </span>
                {pct !== null && pct !== undefined ? (
                  <span className="w-24 shrink-0">
                    <span className="block h-1.5 overflow-hidden rounded-full bg-slate-200">
                      <span className="block h-full rounded-full bg-emerald-500"
                            style={{ width: `${Math.min(100, pct)}%` }} />
                    </span>
                    <span className="mt-0.5 block text-[0.65rem] text-slate-500">
                      {pct}% of {money(a.target, ccy)}
                    </span>
                  </span>
                ) : null}
                <span className="text-right text-sm">
                  <strong className="text-slate-900">{money(a.received, ccy)}</strong>
                  <span className="block text-xs text-slate-500">
                    of {money(a.pledged, ccy)} pledged
                  </span>
                </span>
              </li>
            );
          })}
        </ol>
      ) : (
        <p className="p-3 text-sm text-slate-500" data-testid="leaderboard-empty">
          No assembly has pledged to this appeal yet.
        </p>
      )}

      {Object.keys(data.conversions || {}).length ? (
        <p className="flex items-start gap-1.5 text-xs text-slate-500" data-testid="leaderboard-fx">
          <Info className="mt-0.5 h-3 w-3 shrink-0" />
          <span>
            Shown in {ccy}. {Object.entries(data.conversions).map(([k, v]) =>
              `${k} at ${v.rate}`).join(', ')}. Each assembly's own currency is kept
            on its records unchanged.
          </span>
        </p>
      ) : null}
      </div>
    </div>
  );
};

export default AssemblyLeaderboard;
